import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Hash, CornerDownRight, MessageSquare } from "lucide-react";

const REACTIONS = ["🔥", "💪", "👏"];

function ReactionBar({ m, onReact }) {
  return (
    <div className="flex gap-1 mt-1">
      {REACTIONS.map((e) => {
        const count = (m.reactions?.[e] || []).length;
        return (
          <button key={e} data-testid={`react-${m.id}-${e}`} onClick={() => onReact(m.id, e)} className={`text-xs rounded-full px-2 py-0.5 border ${count ? "border-[#ccff0c]/40 bg-[#ccff0c]/10" : "border-border bg-card"}`}>
            {e} {count > 0 ? count : ""}
          </button>
        );
      })}
    </div>
  );
}

function Avatar({ name, small }) {
  return (
    <div className={`${small ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs"} rounded-full bg-secondary flex items-center justify-center font-bold flex-shrink-0`}>
      {(name || "A")[0].toUpperCase()}
    </div>
  );
}

function MessageHeader({ m }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold">{m.user_name}</span>
      {m.is_admin && <span className="text-[10px] rd-lime">COACH</span>}
      <span className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("general");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const endRef = useRef(null);

  useEffect(() => { api.get("/community/channels").then(({ data }) => setChannels(data)).catch(() => {}); }, []);

  const load = (ch) => api.get("/community/messages", { params: { channel: ch } }).then(({ data }) => setMessages(data)).catch(() => {});
  useEffect(() => { load(active); setReplyTo(null); }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const canPost = active !== "announcements" || user?.role === "admin";

  const onSend = async (text, parentId = null) => {
    const body = (text ?? "").trim();
    if (!body) return;
    try {
      const { data } = await api.post("/community/messages", { channel: active, text: body, parent_id: parentId });
      setMessages((m) => [...m, data]);
      if (parentId) { setReplyText(""); setReplyTo(null); } else { setInput(""); }
    } catch (e) {}
  };

  const onReact = async (id, emoji) => {
    try {
      const { data } = await api.post(`/community/messages/${id}/react`, { emoji });
      setMessages((m) => m.map((x) => x.id === id ? { ...x, reactions: data.reactions } : x));
    } catch (e) {}
  };

  const topLevel = messages.filter((m) => !m.parent_id);
  const repliesOf = (id) => messages.filter((m) => m.parent_id === id);

  return (
    <AppShell title="Community" hideNav>
      <div className="flex gap-2 overflow-x-auto rd-scrollbar pb-3">
        {channels.map((c) => (
          <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setActive(c.id)} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs border transition-colors flex items-center gap-1 ${active === c.id ? "bg-[#ccff0c] text-black border-[#ccff0c]" : "bg-card text-muted-foreground border-border"}`}>
            <Hash className="h-3 w-3" />{c.name}
          </button>
        ))}
      </div>

      <div className="flex flex-col" style={{ height: "calc(100vh - 200px)" }}>
        <div data-testid="community-messages" className="flex-1 overflow-y-auto space-y-4 pb-4">
          {topLevel.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No messages yet. Start the conversation.</p>
          ) : topLevel.map((m) => (
            <div key={m.id} className="flex gap-2">
              <Avatar name={m.user_name} />
              <div className="flex-1">
                <MessageHeader m={m} />
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{m.text}</p>
                <div className="flex items-center gap-3">
                  <ReactionBar m={m} onReact={onReact} />
                  {canPost && (
                    <button data-testid={`reply-btn-${m.id}`} onClick={() => { setReplyTo(replyTo === m.id ? null : m.id); setReplyText(""); }} className="text-[11px] text-muted-foreground hover:text-[#ccff0c] flex items-center gap-1 mt-1">
                      <MessageSquare className="h-3 w-3" /> Reply
                    </button>
                  )}
                </div>

                {/* one-level replies */}
                {repliesOf(m.id).map((r) => (
                  <div key={r.id} className="flex gap-2 ml-8 mt-2">
                    <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground mt-2 flex-shrink-0" />
                    <Avatar name={r.user_name} small />
                    <div className="flex-1">
                      <MessageHeader m={r} />
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.text}</p>
                      <ReactionBar m={r} onReact={onReact} />
                    </div>
                  </div>
                ))}

                {replyTo === m.id && (
                  <div data-testid={`reply-composer-${m.id}`} className="flex items-center gap-2 mt-2 ml-8">
                    <Input
                      data-testid={`reply-input-${m.id}`}
                      autoFocus
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") onSend(replyText, m.id); }}
                      placeholder="Write a reply…"
                      className="bg-card border-border h-9 text-sm"
                    />
                    <Button data-testid={`reply-send-${m.id}`} onClick={() => onSend(replyText, m.id)} disabled={!replyText.trim()} className="h-9 px-3 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0"><Send className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        {canPost ? (
          <div className="flex items-end gap-2 pt-2 border-t border-border">
            <Input data-testid="community-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSend(input); }} placeholder={`Message #${active}`} className="bg-card border-border h-11" />
            <Button data-testid="community-send" onClick={() => onSend(input)} disabled={!input.trim()} className="h-11 w-11 p-0 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0"><Send className="h-5 w-5" /></Button>
          </div>
        ) : (
          <p className="text-center text-xs text-muted-foreground py-3 border-t border-border">Only coaches can post in Announcements.</p>
        )}
      </div>
    </AppShell>
  );
}
